import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Eye, Loader2, Users, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const DECISION_TONE = {
  sangat_direkomendasikan: "bg-[#DCFCE7] text-[#166534]",
  direkomendasikan: "bg-[#E8F6EE] text-[#0B6B3A]",
  dipertimbangkan: "bg-[#FEF9C3] text-[#854D0E]",
  tidak_direkomendasikan: "bg-[#FEE2E2] text-[#991B1B]",
};

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : new Intl.DateTimeFormat("id-ID", { dateStyle: "medium" }).format(d);
}

function collectItems(form) {
  const items = [];
  (form?.sections || []).forEach((section) => {
    if (section.groups) section.groups.forEach((g) => (g.items || []).forEach((it) => items.push(it)));
    else (section.items || []).forEach((it) => items.push(it));
  });
  return items;
}

export default function InterviewScoresManager({ scoped = false }) {
  const [data, setData] = useState(null);
  const [region, setRegion] = useState("all");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/admin/interview-scores", { params: { region } });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [region]);

  useEffect(() => { load(); }, [load]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/interview-scores/export", { params: { region }, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "rekap-hasil-wawancara.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setExporting(false);
    }
  };

  const form = data?.form;
  const decisionLabel = (value) => form?.decisions?.find((d) => d.value === value)?.label || value || "-";
  const scores = data?.scores || [];

  return (
    <div className="space-y-5" data-testid="interview-scores-manager">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <p className="text-sm text-[#6B7280]" data-testid="interview-scores-total">
          {data?.count ?? 0} hasil wawancara tercatat
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {!scoped && (
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              data-testid="interview-scores-region-filter"
              className="rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm font-semibold text-[#1F2937]"
            >
              <option value="all">Seluruh Wilayah</option>
              {(data?.available_regions || []).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          )}
          <button
            type="button"
            onClick={exportExcel}
            disabled={exporting || scores.length === 0}
            data-testid="export-interview-scores"
            className="flex items-center gap-2 rounded-xl bg-[#0B6B3A] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#07532D] disabled:opacity-50"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Ekspor Excel
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] text-left text-sm" data-testid="interview-scores-table">
            <thead className="border-b border-gray-100 bg-[#F9FAFB] text-xs text-[#6B7280]">
              <tr>
                <th className="px-4 py-3 font-semibold">No</th>
                <th className="px-4 py-3 font-semibold">CPM-ID</th>
                <th className="px-4 py-3 font-semibold">Nama Peserta</th>
                <th className="px-4 py-3 font-semibold">Perguruan Tinggi</th>
                <th className="px-4 py-3 font-semibold">Wilayah</th>
                <th className="px-4 py-3 text-center font-semibold">Total Skor</th>
                <th className="px-4 py-3 font-semibold">Rekomendasi</th>
                <th className="px-4 py-3 font-semibold">Pewawancara</th>
                <th className="px-4 py-3 font-semibold">Tanggal</th>
                <th className="px-4 py-3 text-right font-semibold">Rincian</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-[#27AE60]" /></td></tr>
              ) : scores.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-sm text-[#6B7280]" data-testid="interview-scores-empty">
                  <Users className="mx-auto mb-2 h-6 w-6 text-gray-300" />
                  Belum ada hasil wawancara pada wilayah ini.
                </td></tr>
              ) : scores.map((score, index) => (
                <tr key={score.id} className="border-b border-gray-50 hover:bg-[#F9FAFB]">
                  <td className="px-4 py-3 font-semibold text-[#6B7280]">{index + 1}</td>
                  <td className="px-4 py-3 font-mono text-xs font-bold text-[#0B6B3A]">{score.cpm_id}</td>
                  <td className="px-4 py-3 font-bold text-[#1F2937]">{score.candidate_name}</td>
                  <td className="px-4 py-3 text-[#6B7280]">{score.campus}</td>
                  <td className="px-4 py-3 text-[#6B7280]">{score.region}</td>
                  <td className="px-4 py-3 text-center font-display font-extrabold text-[#0B6B3A]">{score.total}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${DECISION_TONE[score.decision] || "bg-gray-100 text-gray-600"}`}>
                      {decisionLabel(score.decision)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#6B7280]">{score.interviewer_name}</td>
                  <td className="px-4 py-3 text-xs text-[#6B7280]">{formatDate(score.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => setDetail(score)} data-testid={`view-interview-score-${score.id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-[#E8F6EE] px-3 py-1.5 text-xs font-bold text-[#0B6B3A] hover:bg-[#D3EFDF]">
                      <Eye className="h-3.5 w-3.5" /> Lihat
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detail && form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="interview-score-detail-modal">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white">
            <div className="sticky top-0 flex items-center justify-between border-b border-gray-100 bg-white px-6 py-4">
              <div>
                <h3 className="font-display text-lg font-extrabold text-[#1F2937]">{detail.candidate_name}</h3>
                <p className="font-mono text-xs font-semibold text-[#0B6B3A]">{detail.cpm_id} · {detail.region}</p>
              </div>
              <button type="button" onClick={() => setDetail(null)} data-testid="close-interview-score-detail" className="rounded-lg p-2 text-[#6B7280] hover:bg-gray-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-5 p-6">
              {(form.sections || []).map((section) => (
                <div key={section.id}>
                  <div className="flex items-center justify-between border-b border-gray-100 pb-1.5">
                    <h4 className="text-sm font-bold text-[#1F2937]">{section.title}</h4>
                    <span className="text-xs font-semibold text-[#0B6B3A]">Subtotal {detail.breakdown?.[section.id] ?? "-"}</span>
                  </div>
                  <div className="mt-2 space-y-2">
                    {(section.groups ? section.groups.flatMap((g) => g.items) : section.items).map((item) => (
                      <div key={item.key} className="flex items-start justify-between gap-4 text-sm">
                        <span className="text-[#6B7280]">{item.label}</span>
                        <span className="shrink-0 font-bold text-[#1F2937]">{detail.scores?.[item.key] ?? "-"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between rounded-xl bg-[#0B6B3A] px-5 py-3 text-white">
                <span className="text-sm font-bold">TOTAL SKOR AKHIR</span>
                <span className="font-display text-xl font-extrabold">{detail.total}</span>
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">Rekomendasi</p>
                <p className="mt-0.5 font-semibold text-[#1F2937]">{decisionLabel(detail.decision)}</p>
              </div>
              {detail.notes && (
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">Catatan / Alasan Utama</p>
                  <p className="mt-0.5 text-sm text-[#1F2937]">{detail.notes}</p>
                </div>
              )}
              <p className="text-xs text-[#6B7280]">Dinilai oleh {detail.interviewer_name} · {formatDate(detail.created_at)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
